import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Search,
  User,
  Phone,
  FileText,
  Eye,
  Edit,
  Loader2,
  IndianRupee,
  MoreVertical,
  Package,
  Filter,
  ChevronDown,
  UserPlus,
  Pencil,
  RefreshCw,
  ArrowLeftRight,
  CheckCircle,
  Upload,
  FileSpreadsheet,
  Trash2,
  Wallet,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addEpcOrderThunk,
  addNewCustomer,
  addRoundOffToPayment,
  createEpcQuote,
  deleteCustomer,
  deductSalesRepCommission,
  fetchSalesRepsUnderCSE,
  getDetailsOfRegisteredUserWithPaymentDetails,
  getEpcMetaData,
  getSalesRep,
  resetCreateEpcQuoteState,
  setSalesRepOptions,
  setSelectedCSE,
  setSelectedSalesRep,
  setSelectedStatus,
  updateEpcOrderThunk,
  updateProductRegistration,
  updateQuoteAdjustmentAmount,
} from "../../store/slices/epcSlice";
import { getEinvoicesByCustomerId } from "../../store/slices/einvoiceSlice";
import {
  ICreateQuotationForm,
  IproductRegistration,
  IRegisteredUser,
  IRegisteredUserWithDetails,
} from "../../models/epcCustomer.model";
import AddCustomerModal from "./AddCustomerModal";
import CustomerDetailsModal from "./CustomerDetailsModal";
import EditCustomerModal from "./EditCustomerModal";
import CreateQuotationModal from "./CreateQuotationModal";
import PaginationControls from "../pagination/PaginationControls";
import AddEpcDocsModal from "./AddEpcDocsModal";
import ShowRegisteredProductModal from "./ShowRegisteredProductModal";
import AddPaymentForm from "./AddPaymentForm";
import SearchInput from "../Search/SearchInput";
import Button, { ButtonType } from "../Assets/Button";
import toast from "react-hot-toast";
import AddAdjustmentModal from "./AddAdjustmentModal";
import DeductCommissionModal from "./DeductCommissionModal";
import AddRoundOffModal from "./AddRoundOffModal";
import AddEpcOrderModal from "./AddEpcOrderModal";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";
import { getAllBOMs } from "../../store/slices/bomSlice";
import useOutsideClick from "../Assets/useOutsideClick";
import ConfirmCustomerAndQuoteModal from "./ConfirmCustomerAndQuoteModal";
import ConfirmationModal from "../Assets/ConfirmationModal";
import QuoteStatusBadge, {
  QuoteStatusLabels,
} from "../../utils/getQuoteStatusLabel";
import { useSearchParams } from "react-router-dom";
import InvoicePdfButton, {
  runInvoicePdfAction,
} from "../PaymentValidation/InvoicePdfButton";
import { InvoiceData } from "../../models/invoice/invoice.model";

type CustomerInvoice = {
  id?: string;
  _id?: string;
  invoiceNumber?: string;
  taxProResponse?: InvoiceData["taxProResponse"];
};

const getCustomerInvoiceErrors = (invoice: CustomerInvoice) =>
  invoice.taxProResponse?.InfoDtls?.flatMap(
    (info) =>
      info.Desc?.map((error) => error.ErrorMessage).filter(
        (message): message is string => Boolean(message?.trim()),
      ) || [],
  ) || [];

type ActiveFilter =
  | "status"
  | "salesRep"
  | "dateRange"
  | "panelBrand"
  | "plantSize"
  | "registrationStatus";

const getPendingPaymentAmount = (
  customer: IRegisteredUserWithDetails | null,
): number =>
  customer?.currentPayment?.payments?.reduce(
    (total, entry) =>
      entry.status === "pending" ? total + Number(entry.amount || 0) : total,
    0,
  ) ?? 0;

const getRemainingPayment = (
  customer: IRegisteredUserWithDetails | null,
): number | undefined => {
  if (!customer) return undefined;

  const payment = customer.currentPayment;
  const totalBillAmount = payment?.totalBillAmount ?? customer.quote?.totalCost;
  if (totalBillAmount === undefined || totalBillAmount === null) {
    return undefined;
  }

  return (
    Number(totalBillAmount) -
    Number(payment?.paymentApproved ?? 0) -
    getPendingPaymentAmount(customer) -
    Number(payment?.roundOffAmount ?? 0) -
    Number(payment?.quote?.adjustmentAmount ?? 0)
  );
};

const CustomerManager: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmation] = useState(false);
  const [isLoanConfirmationOpen, setIsLoanConfirmationOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState<
    string | undefined
  >();
  const [appliedEndDate, setAppliedEndDate] = useState<string | undefined>();

  const [PanelBrandFilter, setPanelBrandFilter] = useState<string>("");
  const [plantSizeFilter, setPlantSizeFilter] = useState<string>("");
  const [registrationStatusFilter, setRegistrationStatusFilter] =
    useState<string>("");
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const selectedActionitemsRef = useRef<{ [key: number]: JSX.Element }>({});
  const selectedActionItemIndexRef = useRef<number | null>(null);

  useOutsideClick(
    menuRef,
    () => setOpenActionMenu(null),
    "[data-menu-trigger]",
  );

  const totalPagesOfRegisteredUsers = useSelector(
    (state: RootState) => state.epc.totalPagesOfRegisteredUsers,
  );

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const isCreatingQuotation = useSelector(
    (state: RootState) => state.epc.loadingState.creatingEpcQuoteLoading,
  );

  const fetchtingCutomerDetailsLoading = useSelector(
    (state: RootState) => state.epc.loadingState.fetchtingCutomerDetailsLoading,
  );

  const isdeletingCustomer = useSelector(
    (state: RootState) => state.epc.loadingState.deleteCustomerLoading,
  );

  const isCustomerUpdating = useSelector(
    (state: RootState) =>
      state.epc.loadingState.updatingRegisteredCustomerLoading,
  );
  const isUpdatingProduct = useSelector(
    (state: RootState) =>
      state.epc.loadingState.updatingProductRegistrationLoading,
  );
  const [isRoundOffConfirmOpen, setIsRoundOffConfirmOpen] = useState(false);
  const [selectedCustomerForRoundOff, setSelectedCustomerForRoundOff] =
    useState<IRegisteredUserWithDetails | null>(null);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [selectedCustomerForAdjustment, setSelectedCustomerForAdjustment] =
    useState<IRegisteredUserWithDetails | null>(null);
  const [isDeductCommissionModalOpen, setIsDeductCommissionModalOpen] =
    useState(false);
  const [
    selectedCustomerForCommissionDeduction,
    setSelectedCustomerForCommissionDeduction,
  ] = useState<IRegisteredUserWithDetails | null>(null);

  const [selectedCustomer, setSelectedCustomer] =
    useState<IRegisteredUserWithDetails | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch: RootDispatch = useDispatch();
  const initialSearchParams = useRef(new URLSearchParams(searchParams));
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    const params = initialSearchParams.current;
    const availableFilters: ActiveFilter[] = [
      "status",
      "salesRep",
      "dateRange",
      "panelBrand",
      "plantSize",
      "registrationStatus",
    ];
    const queryFilters = (params.get("filters") || "")
      .split(",")
      .filter((filter): filter is ActiveFilter =>
        availableFilters.includes(filter as ActiveFilter),
      );
    const startDate = params.get("startDate") || "";
    const endDate = params.get("endDate") || "";
    const panelBrand = params.get("panelBrand") || "";
    const plantSize = params.get("plantSize") || "";
    const registrationStatus = params.get("registrationStatus") || "";

    setSearchTerm(params.get("search") || "");
    setActiveFilters(
      Array.from(
        new Set([
          ...queryFilters,
          ...(params.has("status") ? ["status" as const] : []),
          ...(params.has("cseID") || params.has("salesRepId")
            ? ["salesRep" as const]
            : []),
          ...(startDate || endDate ? ["dateRange" as const] : []),
          ...(panelBrand ? ["panelBrand" as const] : []),
          ...(plantSize ? ["plantSize" as const] : []),
          ...(registrationStatus ? ["registrationStatus" as const] : []),
        ]),
      ),
    );
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setAppliedStartDate(startDate || undefined);
    setAppliedEndDate(endDate || undefined);
    setPanelBrandFilter(panelBrand);
    setPlantSizeFilter(plantSize);
    setRegistrationStatusFilter(registrationStatus);
    dispatch(setSelectedStatus(params.get("status") || ""));
    dispatch(setSelectedCSE(params.get("cseID") || ""));
    dispatch(setSelectedSalesRep(params.get("salesRepId") || undefined));
    setCurrentPage(Math.max(1, Number(params.get("page")) || 1));
    setInitialized(true);
  }, [dispatch]);

  const role = useSelector((state: RootState) => state.auth.role);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingQuotationOpen, setIsCreatingQuotationOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [selectedCustomerForDocs, setSelectedCustomerForDocs] =
    useState<IRegisteredUserWithDetails | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedCustomerForPayment, setSelectedCustomerForPayment] =
    useState<IRegisteredUserWithDetails | null>(null);
  const [customerInvoices, setCustomerInvoices] = useState<CustomerInvoice[]>(
    [],
  );
  const [showCustomerInvoices, setShowCustomerInvoices] = useState(false);
  const [selectedCustomerForInvoices, setSelectedCustomerForInvoices] =
    useState<IRegisteredUserWithDetails | null>(null);
  const [isLoadingCustomerInvoices, setIsLoadingCustomerInvoices] =
    useState(false);

  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [showCustomerNotFoundModal, setShowCustomerNotFoundModal] =
    useState(false);
  const [pendingQuoteData, setPendingQuoteData] =
    useState<ICreateQuotationForm | null>(null);
  const [isCreatingCustomerAndQuote, setIsCreatingCustomerAndQuote] =
    useState(false);

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedQuoteIdForOrder, setSelectedQuoteIdForOrder] =
    useState<string>("");

  const selectedSalesRep = useSelector(
    (state: RootState) => state.epc.selectedSalesRep,
  );
  const selectedCseID = useSelector(
    (state: RootState) => state.epc.selectedCseIdForCustomerDetails,
  );
  const salesRepList = useSelector(
    (state: RootState) => state.epc.salesReps || [],
  );

  const selectedCSE = useSelector((state: RootState) => state.epc.selectedCSE);
  const selectedStatus = useSelector(
    (state: RootState) => state.epc.selectedStatus,
  );
  const salesRepOptions = useSelector(
    (state: RootState) => state.epc.salesRepOptions || [],
  );
  const cseWithSalesReps = useSelector(
    (state: RootState) => state.epc.cseWithSalesReps || [],
  );

  const registeredCustomersAllInfo = useSelector(
    (state: RootState) => state.epc.registeredUserInfoWithDetails,
  );
  const isAddingRoundOff = useSelector(
    (state: RootState) => state.epc.loadingState.addingRoundOffLoading,
  );
  const isUpdatingAdjustment = useSelector(
    (state: RootState) =>
      state.epc.loadingState.updatingAdjustmentAmountLoading,
  );
  const isDeductingCommission = useSelector(
    (state: RootState) =>
      state.epc.loadingState.deductSalesRepCommissionLoading,
  );
  const isAddingEpcOrder = useSelector(
    (state: RootState) => state.epc.loadingState.addingEpcOrderLoading,
  );
  const createEpcQuoteStatus = useSelector(
    (state: RootState) => state.epc.createEpcQuoteStatus,
  );
  const createEpcQuoteError = useSelector(
    (state: RootState) => state.epc.createEpcQuoteError,
  );

  const handleViewCustomerInvoices = async (
    customer: IRegisteredUserWithDetails,
  ) => {
    if (!customer._id) {
      toast.error("Unable to load invoices without a customer ID.");
      return;
    }

    const previewWindow = window.open("", "_blank");
    setIsLoadingCustomerInvoices(true);
    try {
      const response = await dispatch(
        getEinvoicesByCustomerId({ customerId: customer._id }),
      ).unwrap();
      if (response.success === false) {
        throw new Error(response.message || "Unable to load customer invoices.");
      }
      if (!Array.isArray(response.data)) {
        throw new Error("The customer invoice list could not be loaded.");
      }

      const invoices = response.data as CustomerInvoice[];
      if (invoices.length === 0) {
        previewWindow?.close();
        toast.error("No invoices found for this customer.");
        return;
      }

      if (invoices.length === 1) {
        const invoiceId = invoices[0].id || invoices[0]._id;
        if (!invoiceId) {
          throw new Error("The invoice does not have an invoice ID.");
        }
        await runInvoicePdfAction({
          dispatch,
          invoiceId,
          mode: "open",
          fallbackCustomerName: customer.customerName,
          previewWindow,
        });
        return;
      }

      previewWindow?.close();
      setSelectedCustomerForInvoices(customer);
      setCustomerInvoices(invoices);
      setShowCustomerInvoices(true);
    } catch (error) {
      previewWindow?.close();
      console.error("Failed to load customer invoices:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to load customer invoices. Please try again.",
      );
    } finally {
      setIsLoadingCustomerInvoices(false);
    }
  };

  const fetchCustomers = useCallback(
    (page: number = 1) => {
      return dispatch(
        getDetailsOfRegisteredUserWithPaymentDetails({
          salesRepId: selectedSalesRep,
          status: selectedStatus,
          cseID: selectedCSE,
          search: searchTerm,
          page,
          startDate: appliedStartDate,
          endDate: appliedEndDate,
          panelBrand: PanelBrandFilter || undefined,
          plantSize: plantSizeFilter || undefined,
          registrationStatus: registrationStatusFilter || undefined,
        }),
      );
    },
    [
      dispatch,
      selectedSalesRep,
      selectedCSE,
      selectedStatus,
      searchTerm,
      appliedStartDate,
      appliedEndDate,
      PanelBrandFilter,
      plantSizeFilter,
      registrationStatusFilter,
    ],
  );

  useEffect(() => {
    if (!initialized) return;

    const params = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value?: string) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    };

    setOrDelete("search", searchTerm.trim());
    setOrDelete("status", selectedStatus);
    setOrDelete("cseID", selectedCSE);
    setOrDelete("salesRepId", selectedSalesRep);
    setOrDelete("startDate", appliedStartDate);
    setOrDelete("endDate", appliedEndDate);
    setOrDelete("panelBrand", PanelBrandFilter);
    setOrDelete("plantSize", plantSizeFilter);
    setOrDelete("registrationStatus", registrationStatusFilter);
    setOrDelete(
      "filters",
      activeFilters.length ? activeFilters.join(",") : undefined,
    );
    setOrDelete("page", currentPage > 1 ? String(currentPage) : undefined);

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    initialized,
    searchParams,
    setSearchParams,
    searchTerm,
    selectedStatus,
    selectedCSE,
    selectedSalesRep,
    appliedStartDate,
    appliedEndDate,
    PanelBrandFilter,
    plantSizeFilter,
    registrationStatusFilter,
    activeFilters,
    currentPage,
  ]);

  useEffect(() => {
    if (!selectedCSE) return;
    const selected = cseWithSalesReps.find(
      (item) => item.cse._id === selectedCSE,
    );
    dispatch(setSalesRepOptions(selected?.salesReps || []));
  }, [dispatch, selectedCSE, cseWithSalesReps]);

  const handleEpcOrderSubmit = async (
    orderData: any,
    createNewOrder = false,
  ) => {
    try {
      if (selectedCustomer?.quote?.orderCreatedDate && !createNewOrder) {
        await dispatch(updateEpcOrderThunk(orderData)).unwrap();
      } else {
        await dispatch(addEpcOrderThunk(orderData)).unwrap();
      }
      setIsOrderModalOpen(false);
      fetchCustomers(currentPage);
    } catch (error: any) {
      toast.error(error);
    }
  };

  const handleOpenPaymentModal = (customer: IRegisteredUserWithDetails) => {
    setSelectedCustomerForPayment(customer);
    setIsPaymentModalOpen(true);
  };

  const handleOpenDocsModal = (customer: IRegisteredUserWithDetails) => {
    setSelectedCustomerForDocs(customer);
    setIsDocsModalOpen(true);
  };

  const handleShowProductRegistration = (
    customer: IRegisteredUserWithDetails,
  ) => {
    setSelectedCustomer(customer ?? null);
    setIsInfoModalOpen(true);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const statusKey = e.target.value;
    dispatch(setSelectedStatus(statusKey));
    setCurrentPage(1);
  };

  const handleCSEChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cseId = e.target.value;
    dispatch(setSelectedCSE(cseId));
    dispatch(setSelectedSalesRep(undefined));

    const selected = cseWithSalesReps.find((item) => item.cse._id === cseId);
    dispatch(setSalesRepOptions(selected?.salesReps || []));
    setCurrentPage(1);
  };

  const handleSalesRepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setSelectedSalesRep(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!initialized) return;
    fetchCustomers(currentPage);
  }, [
    initialized,
    dispatch,
    searchTerm,
    appliedStartDate,
    appliedEndDate,
    currentPage,
    selectedSalesRep,
    selectedCSE,
    selectedStatus,
    selectedCseID,
    PanelBrandFilter,
    plantSizeFilter,
    registrationStatusFilter,
    fetchCustomers,
  ]);

  const handleNextPage = async () => {
    if (currentPage >= totalPagesOfRegisteredUsers) return;
    setIsNextLoading(true);
    try {
      const nextPage = currentPage + 1;
      await fetchCustomers(nextPage);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error fetching next page:", error);
    } finally {
      setIsNextLoading(false);
    }
  };

  const hasRoundOff = (customer: IRegisteredUserWithDetails) => {
    const payment = customer.currentPayment;
    return (
      payment?.roundOffAmount !== undefined && payment.roundOffAmount !== 0
    );
  };

  const shouldShowRoundOffButton = (customer: IRegisteredUserWithDetails) => {
    const payment = customer.currentPayment;
    if (!payment) return false;
    const diff = Math.abs(payment.totalBillAmount - payment.paymentApproved);
    return diff <= 500 && diff !== 0 && !hasRoundOff(customer);
  };

  const getRoundOffAmount = (customer: IRegisteredUserWithDetails | null) => {
    const payment = customer?.currentPayment;
    if (!payment) return 0;
    return payment.totalBillAmount - payment.paymentApproved;
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    try {
      const prevPage = currentPage - 1;
      await fetchCustomers(prevPage);
      setCurrentPage(prevPage);
    } catch (error) {
      console.error("Error fetching previous page:", error);
    } finally {
      setIsPrevLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getSalesRep({}));
    dispatch(getEpcMetaData());
    dispatch(fetchProducts());
    if (role !== "Commission Dealer" && role !== "Channel Partner") {
      dispatch(fetchDropdownData());
    }
    dispatch(getAllBOMs());
    dispatch(fetchSalesRepsUnderCSE());
  }, [dispatch, role]);

  const handleOpenEditModal = (customer: IRegisteredUserWithDetails) => {
    setIsEditModalOpen(true);
    setSelectedCustomer(customer);
  };

  const handleAddCustomer = async (customerData: IRegisteredUser) => {
    setIsCreatingCustomer(true);
    try {
      const res = await dispatch(addNewCustomer(customerData)).unwrap();

      if (res && res.success) {
        setIsModalOpen(false);
      }

      await fetchCustomers(currentPage);
    } catch (error) {
      console.error("Error creating customer:", error);
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handleApplyDateFilter = () => {
    if (draftStartDate && draftEndDate) {
      const start = new Date(draftStartDate);
      const end = new Date(draftEndDate);
      if (start > end) {
        alert("Start date cannot be after end date");
        return;
      }
    }
    setAppliedStartDate(draftStartDate || undefined);
    setAppliedEndDate(draftEndDate || undefined);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleSeeDetails = (
    customer: IRegisteredUserWithDetails,
    index: number,
  ) => {
    selectedActionItemIndexRef.current = index;
    setSelectedCustomer(customer ?? null);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    selectedActionItemIndexRef.current = null;
    setIsDetailsModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleAddRoundOff = async () => {
    try {
      if (!selectedCustomerForRoundOff) return;
      const payment = selectedCustomerForRoundOff.currentPayment;
      if (!payment) return;
      const diff = payment.totalBillAmount - payment.paymentApproved;
      await dispatch(
        addRoundOffToPayment({
          customerMobileNumber: selectedCustomerForRoundOff.mobileNumber,
          roundOffAmount: diff,
        }),
      ).unwrap();
      await fetchCustomers(currentPage);
      setOpenActionMenu(null);
      setIsRoundOffConfirmOpen(false);
      setSelectedCustomerForRoundOff(null);
    } catch (error) {
      console.error("Error adding round-off:", error);
    }
  };

  const handleAddAdjustment = async (amount: number, remark: string) => {
    if (!selectedCustomerForAdjustment?.quote?._id) {
      toast.error("Quote not found");
      return;
    }
    try {
      await dispatch(
        updateQuoteAdjustmentAmount({
          quoteId: selectedCustomerForAdjustment.quote._id,
          adjustmentAmount: amount,
          adjustmentRemark: remark,
        }),
      ).unwrap();
      await fetchCustomers(currentPage);
      setIsAdjustmentModalOpen(false);
      setSelectedCustomerForAdjustment(null);
    } catch (error) {
      console.error("Error updating adjustment amount:", error);
      toast.error("Failed to update adjustment amount");
    }
  };

  const handleDeductCommission = async (amount: number) => {
    const customer = selectedCustomerForCommissionDeduction;
    const salesRepresentative = customer?.salesRepresentative;
    const salesRepId =
      typeof salesRepresentative === "string"
        ? salesRepresentative
        : salesRepresentative?._id;

    if (!customer?._id || !salesRepId) {
      toast.error("Customer or sales representative not found");
      return;
    }

    try {
      await dispatch(
        deductSalesRepCommission({
          salesRepId,
          customerId: customer._id,
          amount,
        }),
      ).unwrap();
    } catch (error) {
      return;
    }

    setIsDeductCommissionModalOpen(false);
    setSelectedCustomerForCommissionDeduction(null);
    toast.success("Commission deducted successfully");

    try {
      await fetchCustomers(currentPage);
    } catch {
      toast.error(
        "Commission was deducted, but the customer list could not be refreshed",
      );
    }
  };

  const handleUpdateProductRegistration = async (
    updatedData: IproductRegistration,
    geoPhotoFile?: File | null,
  ) => {
    if (!selectedCustomer?._id) return;
    try {
      const formData = new FormData();
      if (updatedData.name) formData.append("name", updatedData.name);
      if (updatedData.consumerNumber)
        formData.append("consumerNumber", updatedData.consumerNumber);
      if (updatedData.address) formData.append("address", updatedData.address);
      if (updatedData.area) formData.append("area", updatedData.area);
      if (updatedData.registrationNumber)
        formData.append("registrationNumber", updatedData.registrationNumber);

      if (updatedData.installationDate) {
        const dateValue =
          updatedData.installationDate instanceof Date
            ? updatedData.installationDate.toISOString()
            : updatedData.installationDate;
        formData.append("installationDate", dateValue);
      }

      if (updatedData.panelMake)
        formData.append("panelMake", updatedData.panelMake);
      if (updatedData.panelCapacity)
        formData.append("panelCapacity", updatedData.panelCapacity);
      if (updatedData.numberOfModules)
        formData.append("numberOfModules", updatedData.numberOfModules);
      if (updatedData.panelVoltage)
        formData.append("panelVoltage", updatedData.panelVoltage);
      if (updatedData.inverterMake)
        formData.append("inverterMake", updatedData.inverterMake);
      if (updatedData.inverterCapacity)
        formData.append("inverterCapacity", updatedData.inverterCapacity);
      if (updatedData.inverterVoltage)
        formData.append("inverterVoltage", updatedData.inverterVoltage);
      if (updatedData.totalSolarCapacity)
        formData.append("totalSolarCapacity", updatedData.totalSolarCapacity);
      if (updatedData.panelBarcodes)
        formData.append(
          "panelBarcodes",
          JSON.stringify(updatedData.panelBarcodes),
        );
      if (updatedData.inverterBarcodes)
        formData.append(
          "inverterBarcodes",
          JSON.stringify(updatedData.inverterBarcodes),
        );
      if (geoPhotoFile) formData.append("geoPhoto", geoPhotoFile);

      await dispatch(
        updateProductRegistration({
          customerId: selectedCustomer._id,
          formData,
        }),
      ).unwrap();
      await fetchCustomers(currentPage);
    } catch (error) {
      console.error("Error updating product registration:", error);
    }
  };

  const handleCreateQuotation = async (quoteData: ICreateQuotationForm) => {
    try {
      const result = await dispatch(
        createEpcQuote({
          customerName: quoteData.customerName,
          mobileNumber: quoteData.mobileNumber,
          subsidyAmount: quoteData.subsidyAmount,
          brand: quoteData.brand,
          panelType: quoteData.panelType,
          plantSize: quoteData.plantSize,
          plantCost: quoteData.plantCost,
          meterCost: quoteData.meterCost,
          netMeterCost: quoteData.netMeterCost,
          salesRepresentative: quoteData.salesRepresentative,
          cseID: quoteData.cseID,
        }),
      );
      if (createEpcQuote.fulfilled.match(result)) {
        await fetchCustomers(currentPage);
      }
      setIsCreatingQuotationOpen(false);
    } catch (error) {
      console.error("Error creating quotation:", error);
    }
  };

  const handleCreateCustomerAndQuote = async () => {
    if (!pendingQuoteData) return;
    setIsCreatingCustomerAndQuote(true);
    try {
      const customerData: IRegisteredUser = {
        customerName: pendingQuoteData.customerName,
        mobileNumber: pendingQuoteData.mobileNumber,
        salesRepresentative: pendingQuoteData.salesRepresentative,
        cseID: pendingQuoteData.cseID,
        kyc: [],
      };
      await dispatch(addNewCustomer(customerData)).unwrap();
      await dispatch(createEpcQuote(pendingQuoteData)).unwrap();
      setShowCustomerNotFoundModal(false);
      setPendingQuoteData(null);
      setIsCreatingQuotationOpen(false);
      await fetchCustomers(currentPage);
    } catch (error) {
      console.error("Error creating customer and quotation:", error);
      toast.error("Failed to create customer and quotation");
    } finally {
      setIsCreatingCustomerAndQuote(false);
    }
  };

  useEffect(() => {
    if (createEpcQuoteStatus === "rejected" && createEpcQuoteError) {
      if (
        typeof createEpcQuoteError === "object" &&
        createEpcQuoteError !== null &&
        "type" in createEpcQuoteError &&
        createEpcQuoteError.type === "CUSTOMER_NOT_FOUND"
      ) {
        const errorObj = createEpcQuoteError as {
          type: "CUSTOMER_NOT_FOUND";
          message: string;
          data: ICreateQuotationForm;
        };
        setPendingQuoteData(errorObj.data);
        setShowCustomerNotFoundModal(true);
        dispatch(resetCreateEpcQuoteState());
      } else if (typeof createEpcQuoteError === "string") {
        toast.error(createEpcQuoteError);
      } else if (
        typeof createEpcQuoteError === "object" &&
        createEpcQuoteError !== null &&
        "message" in createEpcQuoteError
      ) {
        toast.error((createEpcQuoteError as { message: string }).message);
      } else {
        toast.error("Failed to create quotation");
      }
    }
  }, [createEpcQuoteStatus, createEpcQuoteError, dispatch]);

  const clearAllFilters = () => {
    setActiveFilters([]);
    setDraftStartDate("");
    setDraftEndDate("");
    setAppliedStartDate(undefined);
    setAppliedEndDate(undefined);
    setRegistrationStatusFilter("");
    setPanelBrandFilter("");
    setPlantSizeFilter("");
    dispatch(setSelectedStatus(""));
    dispatch(setSelectedSalesRep(undefined));
    dispatch(setSelectedCSE(""));
    dispatch(setSalesRepOptions([]));
    setCurrentPage(1);
  };

  const removeFilter = (filter: ActiveFilter) => {
    setActiveFilters((filters) =>
      filters.filter((active) => active !== filter),
    );
    if (filter === "status") dispatch(setSelectedStatus(""));
    if (filter === "salesRep") {
      dispatch(setSelectedSalesRep(undefined));
      dispatch(setSelectedCSE(""));
      dispatch(setSalesRepOptions([]));
    }
    if (filter === "dateRange") {
      setDraftStartDate("");
      setDraftEndDate("");
      setAppliedStartDate(undefined);
      setAppliedEndDate(undefined);
    }
    if (filter === "panelBrand") setPanelBrandFilter("");
    if (filter === "plantSize") setPlantSizeFilter("");
    if (filter === "registrationStatus") setRegistrationStatusFilter("");
    setCurrentPage(1);
  };

  const hideStatusColumn =
    role === "Commission Dealer" ||
    role === "Channel Partner" ||
    role === "account" ||
    role === "LE" ||
    role === "dispatch-lv1" ||
    role === "dispatch-lv2";

  const hideActionColumn =
    role === "account" || role === "dispatch-lv1" || role === "dispatch-lv2";

  const gridTemplate = `
  minmax(0,2fr)
  minmax(0,1.5fr)
  minmax(0,1.5fr)
  100px
  120px
  minmax(0,1.5fr)
  100px
  ${!hideActionColumn ? "60px" : ""}
  ${!hideStatusColumn ? "50px" : ""}
`;

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto p-4">
        <>
          <div className="mb-8">
            <div>
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-4">
                <SearchInput
                  value={searchTerm}
                  placeholder="Search customer name or mobile number…"
                  className="w-full pr-4 py-2 bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary outline-none transition-all text-sm text-gray-700 placeholder:text-gray-400 shadow-sm"
                  onSearch={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                />

                <div className="relative inline-flex items-center border rounded-md px-3 py-2 bg-white focus-within:ring-1 focus-within:ring-blue-500 min-w-[200px] w-full sm:w-auto">
                  <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <select
                    onChange={(e) => {
                      const f = e.target.value as ActiveFilter;
                      if (f && !activeFilters.includes(f)) {
                        setActiveFilters((p) => [...p, f]);
                      }
                      e.target.value = "";
                    }}
                    className="absolute inset-0 w-full h-full text-sm outline-none bg-transparent opacity-0 cursor-pointer"
                  >
                    <option value="" hidden>
                      Add Filter
                    </option>
                    {!activeFilters.includes("status") && (
                      <option value="status">Status</option>
                    )}
                    {!activeFilters.includes("salesRep") &&
                      role !== "Commission Dealer" &&
                      role !== "Channel Partner" && (
                        <option value="salesRep">Sales Rep</option>
                      )}
                    {!activeFilters.includes("dateRange") && (
                      <option value="dateRange">Date Range</option>
                    )}
                    {!activeFilters.includes("panelBrand") && (
                      <option value="panelBrand">Panel Brand</option>
                    )}
                    {!activeFilters.includes("plantSize") && (
                      <option value="plantSize">Plant Size</option>
                    )}
                  </select>
                  <span className="flex-1 ml-2 text-sm text-gray-600 pointer-events-none whitespace-nowrap">
                    Add Filter
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 pointer-events-none" />
                </div>

                {role !== "LE" &&
                  role !== "account" &&
                  role !== "dispatch-lv1" &&
                  role !== "dispatch-lv2" && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                      <Button
                        type={ButtonType.Submit}
                        onClick={() => setIsModalOpen(true)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 whitespace-nowrap"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add Customer
                      </Button>
                      <Button
                        type={ButtonType.Submit}
                        onClick={() => {
                          setSelectedCustomer(null);
                          setIsCreatingQuotationOpen(true);
                        }}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 whitespace-nowrap"
                      >
                        <FileText className="w-4 h-4" />
                        Create Quotation
                      </Button>
                    </div>
                  )}
              </div>

              {activeFilters.length > 0 && (
                <div className="bg-gray-50 border rounded-xl p-5 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-gray-700">
                      Filters
                    </span>
                    <Button
                      type={ButtonType.Cancel}
                      onClick={clearAllFilters}
                      className="text-xs bg-white text-red-500"
                    >
                      Clear all
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {activeFilters.includes("status") && (
                      <>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-600">
                              Status
                            </label>
                            <button
                              type="button"
                              onClick={() => removeFilter("status")}
                              aria-label="Remove status filter"
                              className="p-1 text-gray-400 hover:text-gray-700"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <select
                            value={selectedStatus || ""}
                            onChange={handleStatusChange}
                            className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">All Statuses</option>
                            {Object.entries(QuoteStatusLabels).map(
                              ([key, value]) => (
                                <option key={key} value={key}>
                                  {value}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      </>
                    )}
                    {activeFilters.includes("salesRep") && (
                      <>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-600">
                              CSE
                            </label>
                            <button
                              type="button"
                              onClick={() => removeFilter("salesRep")}
                              aria-label="Remove sales representative filter"
                              className="p-1 text-gray-400 hover:text-gray-700"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <select
                            value={selectedCSE || ""}
                            onChange={handleCSEChange}
                            className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">All CSE</option>
                            {cseWithSalesReps.map((item) => (
                              <option key={item.cse._id} value={item.cse._id}>
                                {item.cse.username}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-600">
                            Sales Representative
                          </label>
                          <select
                            value={selectedSalesRep || ""}
                            onChange={handleSalesRepChange}
                            className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">All Sales Reps</option>
                            {salesRepOptions.map((rep) => (
                              <option key={rep._id} value={rep._id}>
                                {rep.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {activeFilters.includes("panelBrand") && (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-gray-600">
                            Panel Brand
                          </label>
                          <button
                            type="button"
                            onClick={() => removeFilter("panelBrand")}
                            aria-label="Remove panel brand filter"
                            className="p-1 text-gray-400 hover:text-gray-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <SearchInput
                          value={PanelBrandFilter}
                          placeholder="Search panel brand (e.g. Adani, Waaree)"
                          className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onSearch={(value) => {
                            setPanelBrandFilter(value);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                    )}

                    {activeFilters.includes("plantSize") && (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-gray-600">
                            Plant Size (kW)
                          </label>
                          <button
                            type="button"
                            onClick={() => removeFilter("plantSize")}
                            aria-label="Remove plant size filter"
                            className="p-1 text-gray-400 hover:text-gray-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <SearchInput
                          value={plantSizeFilter}
                          placeholder="Plant size (4 or 4.5)"
                          className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onSearch={(value) => {
                            setPlantSizeFilter(value);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                    )}

                    {activeFilters.includes("dateRange") && (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-gray-600">
                            Date Range
                          </label>
                          <button
                            type="button"
                            onClick={() => removeFilter("dateRange")}
                            aria-label="Remove date range filter"
                            className="p-1 text-gray-400 hover:text-gray-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={draftStartDate}
                            onChange={(e) => setDraftStartDate(e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500">to</span>
                          <input
                            type="date"
                            value={draftEndDate}
                            onChange={(e) => setDraftEndDate(e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <Button
                            type={ButtonType.Extra}
                            onClick={handleApplyDateFilter}
                            className="text-xs inline-flex items-center gap-1"
                          >
                            <Search className="w-3.5 h-3.5" />
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto min-h-[350px]">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 hidden lg:block">
              <div
                className="grid gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <div>Customer</div>
                <div>Status</div>
                <div>Sales Rep</div>
                <div>CSE</div>
                <div className="text-center">Plant Size</div>
                <div>Panel Make</div>
                <div className="text-center">Created</div>
                {!hideActionColumn && (
                  <div className="text-center">Actions</div>
                )}
                {!hideStatusColumn && <div className="text-center">Order</div>}
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {fetchtingCutomerDetailsLoading ? (
                <div className="px-6 py-12 text-center text-sm text-gray-500">
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading customers...
                  </span>
                </div>
              ) : registeredCustomersAllInfo?.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-gray-500">
                  No customers found
                </div>
              ) : (
                registeredCustomersAllInfo?.map(
                  (customer: any, index: number) => {
                    const isMenuOpen = openActionMenu === customer._id;
                    const isConfirmed =
                      customer?.isLatestQuoteConfirmed === true;

                    // Shared action menu used by both mobile and desktop rows.
                    // This keeps all row actions consistent across screen sizes.
                    const ACTION_MENU_ITEMS = (
                      <>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleSeeDetails(customer, index);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <Eye className="w-4 h-4 text-gray-500" /> View Details
                        </button>
                        <button
                          type="button"
                          disabled={isLoadingCustomerInvoices}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenActionMenu(null);
                            void handleViewCustomerInvoices(customer);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
                        >
                          {isLoadingCustomerInvoices ? (
                            <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-sky-600" />
                          )}
                          View Invoices
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleOpenEditModal(customer);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <Edit className="w-4 h-4 text-blue-500" /> Edit
                          Customer
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setSelectedCustomer(customer);
                            setIsCreatingQuotationOpen(true);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <FileSpreadsheet className="w-4 h-4 text-indigo-500" />{" "}
                          Create Quotation
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleOpenDocsModal(customer);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <Upload className="w-4 h-4 text-green-500" /> Add Docs
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleShowProductRegistration(customer);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <FileText className="w-4 h-4 text-gray-500" /> View
                          Info
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenPaymentModal(customer);
                            setOpenActionMenu(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <IndianRupee className="w-4 h-4 text-emerald-500" />{" "}
                          Add Payment
                        </button>

                        {(role === "admin" ||
                          role === "bdm" ||
                          role === "CSE" ||
                          role === "bde") && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                              setIsLoanConfirmationOpen(true);
                              setOpenActionMenu(null);
                            }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            <Wallet className="w-4 h-4 text-red-500" />
                            Apply for Loan
                          </button>
                        )}
                        {role !== "Channel Partner" &&
                          role !== "Commission Dealer" &&
                          customer?._id &&
                          (typeof customer.salesRepresentative === "string"
                            ? customer.salesRepresentative
                            : customer.salesRepresentative?._id) && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedCustomerForCommissionDeduction(
                                  customer,
                                );
                                setIsDeductCommissionModalOpen(true);
                                setOpenActionMenu(null);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <IndianRupee className="w-4 h-4 text-red-500" />
                              Deduct from Commission
                            </button>
                          )}
                        {shouldShowRoundOffButton(customer) &&
                          !customer?.quote?.adjustmentAmount &&
                          role !== "Channel Partner" &&
                          role !== "Commission Dealer" && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedCustomerForRoundOff(customer);
                                setIsRoundOffConfirmOpen(true);
                                setOpenActionMenu(null);
                              }}
                              disabled={isAddingRoundOff}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                              <RefreshCw className="w-4 h-4 text-purple-500" />
                              {isAddingRoundOff ? "Processing..." : "Round Off"}
                            </button>
                          )}
                        {!hasRoundOff(customer) &&
                          !customer?.quote?.adjustmentAmount &&
                          role !== "Channel Partner" &&
                          role !== "Commission Dealer" && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedCustomerForAdjustment(customer);
                                setIsAdjustmentModalOpen(true);
                                setOpenActionMenu(null);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <Pencil className="w-4 h-4 text-blue-500" />{" "}
                              Adjustment
                            </button>
                          )}
                        {(role === "admin" || role === "bdm") && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                              setIsDeleteConfirmation(true);
                              setOpenActionMenu(null);
                            }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            <Trash2 className="w-4 h-4 text-gray-500" /> Delete
                            Customer
                          </button>
                        )}
                      </>
                    );

                    const childrenWithoutViewDetails = React.Children.toArray(
                      ACTION_MENU_ITEMS.props.children,
                    ).slice(1);

                    selectedActionitemsRef.current[index] = (
                      <>{childrenWithoutViewDetails}</>
                    );

                    return (
                      <div
                        key={customer?._id}
                        onClick={() => handleSeeDetails(customer, index)}
                        className="px-4 py-4 lg:px-6 hover:bg-gray-50 transition-colors"
                      >
                        {/* Mobile card view: compact customer summary for small screens */}
                        <div className="block lg:hidden bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="min-w-0">
                                  <p className="font-semibold text-sm text-gray-900 truncate">
                                    {customer?.customerName}
                                  </p>
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                    <Phone className="w-3 h-3" />
                                    <span className="truncate">
                                      {customer?.mobileNumber}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex">
                              <div>
                                {
                                  <QuoteStatusBadge
                                    status={customer?.quoteStatus}
                                    quoteId={customer?.latestQuoteId}
                                    onStatusUpdated={(success) => {
                                      if (success) {
                                        fetchCustomers();
                                      }
                                    }}
                                  />
                                }
                              </div>
                              {!hideActionColumn && (
                                <div className="relative">
                                  <button
                                    data-menu-trigger
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenActionMenu(
                                        isMenuOpen ? null : customer._id,
                                      );
                                    }}
                                    className="p-2 rounded-full hover:bg-gray-100 transition"
                                    aria-label="Open actions"
                                  >
                                    <MoreVertical className="w-4 h-4 text-gray-600" />
                                  </button>
                                  {isMenuOpen && (
                                    <div
                                      ref={(el) => {
                                        if (isMenuOpen) menuRef.current = el;
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      className="absolute right-0 top-10 z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
                                    >
                                      {ACTION_MENU_ITEMS}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5 text-sm text-gray-600">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                                Sales Rep
                              </p>
                              <p className="truncate">
                                {typeof customer?.salesRepresentative ===
                                "object"
                                  ? customer?.salesRepresentative?.name
                                  : customer?.salesRepresentative ||
                                    customer?.cseID?.username ||
                                    "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                                CSE
                              </p>
                              <p className="truncate">
                                {customer?.cseID?.username ||
                                  (typeof customer?.salesRepresentative ===
                                    "object" &&
                                    customer?.salesRepresentative?.cseId
                                      ?.username) ||
                                  "-"}
                              </p>
                            </div>

                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                                Plant size
                              </p>
                              <p className="truncate text-gray-600">
                                {customer?.quote?.plantSize
                                  ? `${customer?.quote?.plantSize} kW`
                                  : "-"}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                                Panel make
                              </p>
                              <p className="truncate text-gray-600">
                                {customer?.quote?.brand || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                                Created
                              </p>
                              <p className="truncate text-gray-600">
                                {formatDate(customer?.createdAt || "")}
                              </p>
                            </div>
                            {!hideStatusColumn && (
                              <div className="col-span-2">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2">
                                  Quote status
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                  {customer.quote?.orderConfirmedDate &&
                                  isConfirmed ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCustomer(customer);
                                        setSelectedQuoteIdForOrder(
                                          customer.quote?._id || "",
                                        );
                                        setIsOrderModalOpen(true);
                                      }}
                                      className="flex items-center justify-center w-8 h-8 rounded-full border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 transition-all shadow-sm"
                                      title="Order Finalized"
                                    >
                                      <CheckCircle size={14} />
                                    </button>
                                  ) : customer.quote?.orderCreatedDate &&
                                    isConfirmed ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCustomer(customer);
                                        setSelectedQuoteIdForOrder(
                                          customer.quote?._id || "",
                                        );
                                        setIsOrderModalOpen(true);
                                      }}
                                      className="flex items-center justify-center w-8 h-8 rounded-full border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all shadow-sm"
                                      title="Edit Order Draft"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isConfirmed) {
                                          setSelectedCustomer(customer);
                                          setSelectedQuoteIdForOrder(
                                            customer.latestQuoteId ||
                                              customer.quote?._id ||
                                              "",
                                          );
                                          setIsOrderModalOpen(true);
                                        } else {
                                          toast.error(
                                            "Quotation must be confirmed before creating order",
                                          );
                                        }
                                      }}
                                      className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all ${
                                        isConfirmed
                                          ? "bg-green-50 border-green-200 text-green-600"
                                          : "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                                      }`}
                                      title={
                                        isConfirmed
                                          ? "Click to Create Order"
                                          : "Quotation Not Confirmed"
                                      }
                                    >
                                      <div
                                        className={`w-0 h-0 border-t-[4px] border-t-transparent border-l-[7px] border-b-[4px] border-b-transparent ml-0.5 ${
                                          isConfirmed
                                            ? "border-l-green-600"
                                            : "border-l-gray-400"
                                        }`}
                                      />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Desktop table row: visible on large screens only */}
                        <div
                          className="hidden lg:grid gap-4 items-start lg:items-center"
                          style={{ gridTemplateColumns: gridTemplate }}
                        >
                          <div className="col-span-2 lg:col-span-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className="relative group flex items-center justify-center flex-shrink-0">
                                  <div className="pointer-events-none absolute left-full top-1/2 z-30 ml-2 w-max -translate-y-1/2 rounded-lg border bg-white p-2 text-xs shadow-lg opacity-0 group-hover:opacity-100">
                                    {customer?.registrationRemark?.trim() ||
                                      "No remark provided"}
                                  </div>
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate">
                                    {customer?.customerName}
                                  </div>
                                  <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                    <Phone className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                    <span className="truncate">
                                      {customer?.mobileNumber}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {!hideActionColumn && (
                                <div className="relative lg:hidden">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      setOpenActionMenu(
                                        isMenuOpen ? null : customer._id,
                                      );
                                    }}
                                    className="p-2 rounded-md hover:bg-gray-100 transition"
                                  >
                                    <MoreVertical className="w-4 h-4 text-gray-600" />
                                  </button>
                                  {isMenuOpen && (
                                    <div className="absolute right-0 top-9 z-50 w-44 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                                      {ACTION_MENU_ITEMS}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            <div>
                              <QuoteStatusBadge
                                status={customer?.quoteStatus || "-"}
                                quoteId={customer?.latestQuoteId}
                                onStatusUpdated={(success) => {
                                  if (success) {
                                    fetchCustomers();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-span-1 lg:col-span-1 text-sm text-gray-600 truncate">
                            <p className="text-xs font-medium text-gray-500 lg:hidden mb-1">
                              Sales Rep
                            </p>
                            <div className="flex items-center">
                              <User className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0 lg:hidden" />
                              <span className="text-sm truncate">
                                {typeof customer?.salesRepresentative ===
                                "object"
                                  ? customer?.salesRepresentative?.name
                                  : customer?.salesRepresentative ||
                                    customer?.cseID?.username ||
                                    "-"}
                              </span>
                            </div>
                          </div>
                          <div className="col-span-1 lg:col-span-1 text-sm text-gray-600 truncate">
                            <p className="text-xs font-medium text-gray-500 lg:hidden mb-1">
                              CSE
                            </p>
                            <div className="flex items-center">
                              <User className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0 lg:hidden" />
                              <span className="text-sm truncate">
                                {customer?.cseID?.username ||
                                  (typeof customer?.salesRepresentative ===
                                    "object" &&
                                    customer?.salesRepresentative?.cseId
                                      ?.username) ||
                                  "-"}
                              </span>
                            </div>
                          </div>

                          <div className="col-span-1 lg:col-span-1 lg:text-center text-sm font-medium text-gray-700">
                            <p className="text-xs font-medium text-gray-500 lg:hidden mb-1">
                              Plant Size
                            </p>
                            {customer?.quote?.plantSize ? (
                              <span className="inline-flex items-center px-2.5 py-1 lg:px-0 lg:py-0 rounded-md text-xs font-medium bg-gray-100 lg:bg-transparent text-gray-600">
                                <ArrowLeftRight className="w-3 h-3 mr-1.5 flex-shrink-0 lg:hidden" />
                                <span className="text-blue-600 lg:text-gray-700 font-medium">
                                  {customer?.quote?.plantSize} kW
                                </span>
                              </span>
                            ) : (
                              "-"
                            )}
                          </div>
                          <div className="col-span-1 lg:col-span-1 text-sm text-gray-600 truncate">
                            <p className="text-xs font-medium text-gray-500 lg:hidden mb-1">
                              Panel Make
                            </p>
                            {customer?.quote?.brand ? (
                              <div className="flex items-center text-gray-900 lg:text-gray-600">
                                <Package className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0 lg:hidden" />
                                <span className="text-sm truncate">
                                  {customer?.quote?.brand}
                                </span>
                              </div>
                            ) : (
                              "-"
                            )}
                          </div>
                          <div className="col-span-1 lg:col-span-1 lg:text-center text-xs text-gray-500">
                            <p className="text-xs font-medium text-gray-500 lg:hidden mb-1">
                              Created
                            </p>
                            <span>{formatDate(customer?.createdAt || "")}</span>
                          </div>
                          {/* Desktop action menu */}
                          {!hideActionColumn && (
                            <div className="hidden lg:flex lg:col-span-1 justify-center relative">
                              <button
                                data-menu-trigger
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenActionMenu(
                                    isMenuOpen ? null : customer._id,
                                  );
                                }}
                                className="p-1.5 rounded-full hover:bg-gray-200 focus:outline-none"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>
                              {isMenuOpen && (
                                <div
                                  ref={(el) => {
                                    if (isMenuOpen) menuRef.current = el;
                                  }}
                                  className="absolute top-full right-0 z-[9999] mt-1"
                                >
                                  <div className="w-44 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                                    {ACTION_MENU_ITEMS}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {!hideStatusColumn && (
                            <div className="col-span-1 lg:col-span-1 flex justify-start lg:justify-center items-center mt-2 lg:mt-0">
                              <p className="text-xs font-medium text-gray-500 lg:hidden mb-1 mr-3">
                                Status
                              </p>
                              {customer.quote?.orderConfirmedDate &&
                              isConfirmed ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(customer);
                                    setSelectedQuoteIdForOrder(
                                      customer.quote?._id || "",
                                    );
                                    setIsOrderModalOpen(true);
                                  }}
                                  className="flex items-center justify-center w-8 h-8 rounded border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 transition-all shadow-sm"
                                  title="Order Finalized"
                                >
                                  <CheckCircle size={14} />
                                </button>
                              ) : customer.quote?.orderCreatedDate &&
                                isConfirmed ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(customer);
                                    setSelectedQuoteIdForOrder(
                                      customer.quote?._id || "",
                                    );
                                    setIsOrderModalOpen(true);
                                  }}
                                  className="flex items-center justify-center w-8 h-8 rounded border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all shadow-sm"
                                  title="Edit Order Draft"
                                >
                                  <Pencil size={14} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isConfirmed) {
                                      setSelectedCustomer(customer);
                                      setSelectedQuoteIdForOrder(
                                        customer.latestQuoteId ||
                                          customer.quote?._id ||
                                          "",
                                      );
                                      setIsOrderModalOpen(true);
                                    } else {
                                      toast.error(
                                        "Quotation must be confirmed before creating order",
                                      );
                                    }
                                  }}
                                  className={`flex items-center justify-center w-8 h-8 rounded border transition-all ${
                                    isConfirmed
                                      ? "bg-green-50 border-green-200 text-green-600"
                                      : "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                                  }`}
                                  title={
                                    isConfirmed
                                      ? "Click to Create Order"
                                      : "Quotation Not Confirmed"
                                  }
                                >
                                  <div
                                    className={`w-0 h-0 border-t-[4px] border-t-transparent border-l-[7px] border-b-[4px] border-b-transparent ml-0.5 ${
                                      isConfirmed
                                        ? "border-l-green-600"
                                        : "border-l-gray-400"
                                    }`}
                                  />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )
              )}
            </div>
          </div>
          <PaginationControls
            isNextFetching={isNextLoading}
            isPrevFetching={isPrevLoading}
            currentPage={currentPage}
            totalPages={totalPagesOfRegisteredUsers}
            onNext={handleNextPage}
            onPrev={handlePreviousPage}
          />
        </>
        <AddEpcOrderModal
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          quotationId={selectedQuoteIdForOrder}
          onSubmit={handleEpcOrderSubmit}
          isLoading={isAddingEpcOrder}
          canCreateOrder={selectedCustomer?.canCreateOrder || false}
          address={[
            selectedCustomer?.productRegistration?.address,
            selectedCustomer?.productRegistration?.area,
          ]
            .filter(Boolean)
            .join(", ")}
        />

        <EditCustomerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdated={() => fetchCustomers(currentPage)}
          customer={selectedCustomer}
          isLoading={isCustomerUpdating}
        />
        {isModalOpen && (
          <AddCustomerModal
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleAddCustomer}
            isLoading={isCreatingCustomer}
          />
        )}

        <CustomerDetailsModal
          isOpen={isDetailsModalOpen}
          setIsOpen={setIsDetailsModalOpen}
          onClose={closeDetailsModal}
          customer={selectedCustomer}
          actionItems={
            selectedActionitemsRef.current[
              selectedActionItemIndexRef.current ?? 0
            ]
          }
        />
        <CreateQuotationModal
          isOpen={isCreatingQuotationOpen}
          onClose={() => setIsCreatingQuotationOpen(false)}
          onSubmit={handleCreateQuotation}
          isLoading={isCreatingQuotation}
          customer={selectedCustomer}
          isDynamic={!!selectedCustomer}
        />
        <ConfirmCustomerAndQuoteModal
          isOpen={showCustomerNotFoundModal}
          onClose={() => {
            setShowCustomerNotFoundModal(false);
            setPendingQuoteData(null);
          }}
          onConfirm={handleCreateCustomerAndQuote}
          customerData={{
            customerName: pendingQuoteData?.customerName || "",
            mobileNumber: pendingQuoteData?.mobileNumber || "",
            salesRepresentative: pendingQuoteData?.salesRepresentative,
            cseID: pendingQuoteData?.cseID,
          }}
          isLoading={isCreatingCustomerAndQuote}
          salesRepList={salesRepList}
        />
        <AddEpcDocsModal
          isOpen={isDocsModalOpen}
          onClose={(success) => {
            setIsDocsModalOpen(false);
            if (success) {
              void fetchCustomers(currentPage);
            }
          }}
          customerName={selectedCustomerForDocs?.customerName || ""}
          customerId={selectedCustomerForDocs?._id}
        />
        <ShowRegisteredProductModal
          isOpen={isInfoModalOpen}
          onClose={() => {
            setIsInfoModalOpen(false);
          }}
          customer={selectedCustomer}
          productRegistration={selectedCustomer?.productRegistration}
          onUpdate={handleUpdateProductRegistration}
          isUpdating={isUpdatingProduct}
        />
        {isPaymentModalOpen && selectedCustomerForPayment && (
          <AddPaymentForm
            onClose={() => {
              setIsPaymentModalOpen(false);
              setSelectedCustomerForPayment(null);
            }}
            customerMobileNumber={selectedCustomerForPayment?.mobileNumber}
            remainingAmount={getRemainingPayment(selectedCustomerForPayment)}
            pendingPaymentAmount={getPendingPaymentAmount(
              selectedCustomerForPayment,
            )}
            currentPage={currentPage}
            searchTerm={searchTerm}
            selectedSalesRep={selectedSalesRep}
            selectedCSE={selectedCSE}
            startDate={appliedStartDate}
            endDate={appliedEndDate}
            panelBrand={PanelBrandFilter || undefined}
            plantSize={plantSizeFilter || undefined}
            registrationStatus={registrationStatusFilter || undefined}
          />
        )}

        <AddAdjustmentModal
          isOpen={isAdjustmentModalOpen}
          onClose={() => {
            setIsAdjustmentModalOpen(false);
            setSelectedCustomerForAdjustment(null);
          }}
          onConfirm={handleAddAdjustment}
          customerName={selectedCustomerForAdjustment?.customerName || ""}
          totalBillAmount={
            selectedCustomerForAdjustment?.currentPayment?.totalBillAmount || 0
          }
          paymentApproved={
            selectedCustomerForAdjustment?.currentPayment?.paymentApproved || 0
          }
          remainingAmount={
            (selectedCustomerForAdjustment?.currentPayment?.totalBillAmount ||
              0) -
            (selectedCustomerForAdjustment?.currentPayment?.paymentApproved ||
              0)
          }
          isLoading={isUpdatingAdjustment}
        />
        <DeductCommissionModal
          isOpen={
            isDeductCommissionModalOpen &&
            !!selectedCustomerForCommissionDeduction
          }
          customerName={
            selectedCustomerForCommissionDeduction?.customerName || ""
          }
          isLoading={!!isDeductingCommission}
          onClose={() => {
            setIsDeductCommissionModalOpen(false);
            setSelectedCustomerForCommissionDeduction(null);
          }}
          onConfirm={handleDeductCommission}
        />
        <ConfirmationModal
          isOpen={isLoanConfirmationOpen}
          danger={false}
          onClose={() => {
            setIsLoanConfirmationOpen(false);
          }}
          onConfirm={async () => {
            //TODO:Loan api
          }}
          description="LE will be notified to apply for loan"
          subTitle={`Are you sure you want to apply for a loan for ${selectedCustomer?.customerName}? `}
          confirmText="Yes, Apply"
          cancelText="Cancel"
          loading={isdeletingCustomer}
        />
        <ConfirmationModal
          isOpen={isDeleteConfirmationOpen}
          onClose={() => {
            setIsDeleteConfirmation(false);
          }}
          onConfirm={async () => {
            if (selectedCustomer && selectedCustomer._id) {
              const res = await dispatch(
                deleteCustomer(selectedCustomer?._id),
              ).unwrap();
              if (res.success) {
                setIsDeleteConfirmation(false);
                setSelectedCustomer(null);
                await fetchCustomers(currentPage);
              }
            } else {
              toast.error("No customer selected to delete");
            }
          }}
          subTitle={`All the quotes, payments will be removed of ${selectedCustomer?.customerName}.`}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          loading={isdeletingCustomer}
        />
        <AddRoundOffModal
          isOpen={isRoundOffConfirmOpen && !!selectedCustomerForRoundOff}
          isLoading={isAddingRoundOff}
          customerName={selectedCustomerForRoundOff?.customerName || ""}
          roundOffAmount={
            selectedCustomerForRoundOff
              ? getRoundOffAmount(selectedCustomerForRoundOff)
              : 0
          }
          onClose={() => {
            setIsRoundOffConfirmOpen(false);
            setSelectedCustomerForRoundOff(null);
          }}
          onConfirm={handleAddRoundOff}
        />
        {showCustomerInvoices && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            onClick={() => setShowCustomerInvoices(false)}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-invoices-title"
              className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <h2
                    id="customer-invoices-title"
                    className="text-base font-semibold text-slate-900"
                  >
                    Invoices
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {selectedCustomerForInvoices?.customerName}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close invoices"
                  onClick={() => setShowCustomerInvoices(false)}
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                >
                  <X size={18} />
                </button>
              </header>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {customerInvoices.map((invoice, index) => {
                  const invoiceId = invoice.id || invoice._id || "";
                  const errors = getCustomerInvoiceErrors(invoice);
                  return (
                    <InvoicePdfButton
                      key={invoiceId || `invoice-${index}`}
                      invoiceId={invoiceId}
                      mode="open"
                      fallbackCustomerName={
                        selectedCustomerForInvoices?.customerName
                      }
                      disabled={!invoiceId}
                      onComplete={() => setShowCustomerInvoices(false)}
                      className="flex w-full items-start gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {(isLoading) => (
                        <>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                            {isLoading ? (
                              <Loader2 className="animate-spin" size={18} />
                            ) : (
                              <FileText size={18} />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-slate-900">
                              {invoice.invoiceNumber || `Invoice ${index + 1}`}
                            </span>
                            {errors.length > 0 && (
                              <span className="mt-1 block text-xs text-rose-700">
                                {errors.join(" ")}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </InvoicePdfButton>
                  );
                })}
              </div>
              <footer className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setShowCustomerInvoices(false)}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200"
                >
                  Close
                </button>
              </footer>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerManager;
